import {
	type ContractSuretyType,
	contractSuretyTypeDef,
	contractSuretyTypeToString,
	formatToDollar,
	formatToPercent,
	SuretyAccountId,
	SuretyBondStatus,
} from "@inrev/common";
import { useAdminFetchAccountBondPreviews } from "@portal/api/bond/admin";
import type { SuretyBondPreview } from "@portal/api/bond/agent/types";
import {
	DynamicDataTable,
	type DynamicDataTableColumn,
} from "@portal/components/ui/DynamicDataTable/DynamicDataTable";
import { ErrorDisplay } from "@portal/components/ui/ErrorDisplay";
import { getStatusTagByBondStatus } from "@portal/components/ui/StatusTag";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { SortingState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { formatDateForCsv, formatEmpty } from "../../../utils";

const EMPTY_DATA: SuretyBondPreview[] = [];
const INITIAL_SORTING: SortingState = [{ id: "effectiveDate", desc: true }];

type BondRow = SuretyBondPreview;

const isContractSuretyType = (type: string): type is ContractSuretyType =>
	(contractSuretyTypeDef as readonly string[]).includes(type);

const getColumns = (isMinimalUIEnabled: boolean): DynamicDataTableColumn<BondRow>[] => [
	{
		id: "status",
		accessorKey: "status",
		header: "Status",
		size: 116,
		headerFilter: { type: "checkbox" },
		cell: ({ row }) => getStatusTagByBondStatus(row.original.status),
	},
	{
		id: "type",
		header: "Type",
		size: 77,
		accessorFn: (row) =>
			isContractSuretyType(row.type) ? contractSuretyTypeToString(row.type) : undefined,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "number",
		accessorKey: "number",
		header: "Bond Number",
		size: 115,
		minSize: 115,
		enableResizing: true,
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 130,
		cell: ({ row }) => formatToDollar(row.original.amount),
	},
	{
		id: "rate",
		header: "Rate",
		size: 130,
		accessorKey: "rate",
		cell: ({ row }) => formatToPercent(row.original.rate),
		csv: {
			formatValue: (value) => (typeof value === "number" ? `${value * 100}%` : "--"),
		},
	},
	{
		id: "premium",
		accessorKey: "premium",
		header: "Premium",
		size: 130,
		cell: ({ row }) => formatToDollar(row.original.premium, true),
	},
	{
		id: "principalName",
		accessorKey: "principalName",
		header: "Principal",
		size: 180,
		minSize: 130,
		enableResizing: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "obligeeName",
		accessorKey: "obligeeName",
		header: "Obligee",
		size: 200,
		minSize: 150,
		enableResizing: true,
	},
	{
		id: "description",
		accessorFn: (row) => formatEmpty(isMinimalUIEnabled ? row.name : row.description),
		header: isMinimalUIEnabled ? "Project Name" : "Description",
		size: 200,
		minSize: 150,
		enableResizing: true,
	},
	{
		id: "effectiveDate",
		header: "Effective Date",
		size: 130,
		accessorKey: "effectiveDate",
		enableSorting: true,
		cell: ({ row }) =>
			row.original.effectiveDate !== undefined
				? DateTime.fromISO(row.original.effectiveDate).toLocaleString(DateTime.DATE_MED)
				: "--",
		csv: {
			formatValue: formatDateForCsv,
		},
	},
	{
		id: "expectedCompletionDate",
		header: "Est. Completion Date",
		size: 180,
		minSize: 150,
		accessorKey: "expectedCompletionDate",
		enableSorting: true,
		cell: ({ row }) =>
			row.original.expectedCompletionDate !== undefined
				? DateTime.fromISO(row.original.expectedCompletionDate).toLocaleString(DateTime.DATE_MED)
				: "--",
		csv: {
			formatValue: formatDateForCsv,
		},
	},
	{
		id: "entityTags",
		header: "Tags",
		size: 150,
		minSize: 100,
		enableResizing: true,
		accessorFn: (row) => row.entityTags?.map((tag) => tag.displayName).join(", "),
	},
	// CSV-only columns
	{
		id: "suretyType",
		accessorKey: "suretyType",
		header: "Surety Type",
	},
	{
		id: "carrierDisplayName",
		accessorKey: "carrierDisplayName",
		header: "Carrier",
	},
	{
		id: "agencyCommission",
		accessorKey: "agencyCommission",
		header: "Agency Commission",
	},
];

const CSV_ONLY_COLUMNS = {
	suretyType: false,
	carrierDisplayName: false,
	agencyCommission: false,
};

type AdminAccountBondsTableProps = {
	tableName?: string;
	accountId: SuretyAccountId;
	filterType?: "bid" | "final";
	status?: SuretyBondStatus;
};

export const AdminAccountBondsTable = ({
	accountId,
	tableName,
	filterType,
	status,
}: AdminAccountBondsTableProps) => {
	const {
		adminAccountBondPreviews,
		adminAccountBondPreviewsIsLoading,
		adminAccountBondPreviewsError,
	} = useAdminFetchAccountBondPreviews(accountId);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const columns = useMemo(() => getColumns(isMinimalUIEnabled), [isMinimalUIEnabled]);

	const filteredBonds = useMemo(() => {
		if (!adminAccountBondPreviews) {
			return EMPTY_DATA;
		}

		if (!filterType && !status) {
			return adminAccountBondPreviews;
		}

		return adminAccountBondPreviews.filter((bond) => {
			if (filterType && bond.type !== filterType) return false;
			if (status && bond.status !== status) return false;
			return true;
		});
	}, [adminAccountBondPreviews, filterType, status]);

	if (adminAccountBondPreviewsError) {
		return <ErrorDisplay error={adminAccountBondPreviewsError} />;
	}

	return (
		<div data-testid="admin-account-bonds-table" className="flex flex-col w-full space-y-2">
			<DynamicDataTable
				name={tableName || "admin_account_bonds"}
				columns={columns}
				data={filteredBonds}
				dataLoading={adminAccountBondPreviewsIsLoading}
				initialSorting={INITIAL_SORTING}
				getRowHref={(row) => `/bonds/${row.id}`}
				columnVisibility={CSV_ONLY_COLUMNS}
				tableClassName="shadow-sm rounded-xl"
				maxHeight={400}
				minHeight={400}
				enableExportToCSV
			/>
		</div>
	);
};
